import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useOutletContext } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { getAdminSaleOrders, type AdminSaleOrderWithPhase } from '../lib/api'
import type { SaleEventDetailContext } from './SaleEventDetailLayout'
import { normalizeImageKitUrl } from '@lic/media-urls'

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900 overflow-hidden">
      <div className="px-5 py-3 border-b border-zinc-800">
        <h2 className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">{title}</h2>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  )
}

export default function SaleEventOverviewPage() {
  const { event } = useOutletContext<SaleEventDetailContext>()
  const { id } = useParams<{ id: string }>()
  const { token } = useAuth()
  const [orders, setOrders] = useState<AdminSaleOrderWithPhase[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!id || !token) return
    setLoading(true)
    getAdminSaleOrders(token, id)
      .then(setOrders)
      .finally(() => setLoading(false))
  }, [id, token])

  const pendingCount = orders.filter(o => o.status === 'pending').length
  const confirmedCount = orders.filter(o => o.status === 'confirmed').length
  const revenue = orders
    .filter(o => o.status === 'confirmed')
    .reduce((sum, o) => sum + (o.sale_event_phases?.price ?? 0) * o.quantity, 0)
  const fmtRevenue = new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(revenue)

  function handleExportCSV() {
    const confirmed = orders.filter(o => o.status === 'confirmed')
    const rows = [
      ['Kode Order', 'Nama', 'Phone', 'Email', 'Phase', 'Qty', 'Total', 'Tanggal'],
      ...confirmed.map(o => [
        o.order_code,
        o.buyer_name,
        o.buyer_phone,
        o.buyer_email,
        o.sale_event_phases?.name ?? '-',
        o.quantity,
        (o.sale_event_phases?.price ?? 0) * o.quantity,
        new Date(o.created_at).toLocaleDateString('id-ID'),
      ]),
    ]
    const csv = rows.map(r => r.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${event.title}-buyers.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="p-6 space-y-6">

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: 'Total Order', value: loading ? '—' : orders.length, color: 'text-white' },
          { label: 'Menunggu', value: loading ? '—' : pendingCount, color: 'text-yellow-400' },
          { label: 'Dikonfirmasi', value: loading ? '—' : confirmedCount, color: 'text-green-400' },
          { label: 'Total Revenue', value: loading ? '—' : fmtRevenue, color: 'text-white' },
        ].map(({ label, value, color }) => (
          <div key={label} className="rounded-xl border border-zinc-800 bg-zinc-900 p-4">
            <p className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider mb-1">{label}</p>
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Event info */}
      <Section title="Detail Event">
        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-4">
            {[
              { label: 'Tanggal', value: event.date },
              event.time ? { label: 'Waktu', value: event.time } : null,
              { label: 'Lokasi', value: event.location },
            ].filter(Boolean).map((row) => (
              <div key={row!.label}>
                <p className="text-[11px] text-zinc-500 uppercase tracking-wider mb-0.5">{row!.label}</p>
                <p className="text-sm text-zinc-300">{row!.value}</p>
              </div>
            ))}
          </div>
          {event.description && (
            <div>
              <p className="text-[11px] text-zinc-500 uppercase tracking-wider mb-0.5">Deskripsi</p>
              <p className="text-sm text-zinc-300 whitespace-pre-line">{event.description}</p>
            </div>
          )}
        </div>
      </Section>

      {event.terms && (
        <Section title="Syarat dan Ketentuan">
          <p className="text-sm text-zinc-300 whitespace-pre-line">{event.terms}</p>
        </Section>
      )}

      {event.banner_image_url && (
        <Section title="Banner">
          <img
            src={normalizeImageKitUrl(event.banner_image_url)}
            alt="Banner event"
            className="rounded-lg border border-zinc-700 object-contain max-h-64 w-auto"
          />
        </Section>
      )}

      {/* Export */}
      <div className="flex justify-end">
        <button
          onClick={handleExportCSV}
          disabled={loading || confirmedCount === 0}
          className="px-4 py-2 text-sm text-zinc-400 hover:text-white transition-colors rounded-lg hover:bg-zinc-800 border border-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Export CSV Pembeli
        </button>
      </div>

    </div>
  )
}
